import { useState } from 'react';
import { useAuthStore } from '@/store/auth';
import { useChangePassword } from '@/hooks/useAuth';
import { DataImport } from '@/components/DataImport';
import { ModelTraining } from '@/components/ModelTraining';

export function Settings() {
  const { user, getEffectiveRole } = useAuthStore();
  const effectiveRole = getEffectiveRole();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const changePasswordMutation = useChangePassword();

  const formatLastLogin = (lastLogin: string | null | undefined): string => {
    if (!lastLogin) return '--';
    try {
      const date = new Date(lastLogin);
      return date.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }) + ' CT';
    } catch {
      return '--';
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'PASSWORDS DO NOT MATCH' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'PASSWORD MUST BE AT LEAST 8 CHARACTERS' });
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({ currentPassword, newPassword });
      setMessage({ type: 'success', text: 'PASSWORD CHANGED SUCCESSFULLY' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      setMessage({ type: 'error', text: 'FAILED TO CHANGE PASSWORD' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-mono-800 pb-4">
        <h1 className="text-lg font-medium text-mono-100 tracking-widest">SETTINGS</h1>
      </div>

      {/* Profile */}
      <div className="border border-mono-800">
        <div className="border-b border-mono-800 px-4 py-3">
          <h3 className="text-xs text-mono-300 tracking-widest">PROFILE</h3>
        </div>
        <div className="p-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] text-mono-500 tracking-widest">NAME</p>
            <p className="text-sm text-mono-200 mt-1">{user?.full_name || '--'}</p>
          </div>
          <div>
            <p className="text-[10px] text-mono-500 tracking-widest">EMAIL</p>
            <p className="text-sm text-mono-200 mt-1">{user?.email}</p>
          </div>
          <div>
            <p className="text-[10px] text-mono-500 tracking-widest">ROLE</p>
            <p className="text-sm text-mono-200 mt-1 uppercase">{effectiveRole}</p>
          </div>
          <div>
            <p className="text-[10px] text-mono-500 tracking-widest">LAST LOGIN</p>
            <p className="text-sm text-mono-200 mt-1">{formatLastLogin(user?.last_login)}</p>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div className="border border-mono-800">
        <div className="border-b border-mono-800 px-4 py-3">
          <h3 className="text-xs text-mono-300 tracking-widest">CHANGE PASSWORD</h3>
        </div>
        <div className="p-4">
          {message && (
            <div className={`mb-4 p-3 border ${
              message.type === 'success' ? 'border-mono-600' : 'border-mono-500'
            }`}>
              <p className="text-[10px] text-mono-300 tracking-wider">{message.text}</p>
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4 max-w-sm">
            <div>
              <label className="block text-[10px] text-mono-500 tracking-widest mb-2">
                CURRENT PASSWORD
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-xs focus:border-mono-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-[10px] text-mono-500 tracking-widest mb-2">
                NEW PASSWORD
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-xs focus:border-mono-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-[10px] text-mono-500 tracking-widest mb-2">
                CONFIRM PASSWORD
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-xs focus:border-mono-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={changePasswordMutation.isPending}
                className="px-4 py-2 border border-mono-600 text-mono-300 text-xs tracking-widest hover:border-mono-400 hover:text-mono-100 disabled:opacity-50 transition-colors"
              >
                {changePasswordMutation.isPending ? 'UPDATING...' : 'UPDATE PASSWORD'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Data Import */}
      {effectiveRole === 'admin' || effectiveRole === 'superadmin' && <DataImport />}

      {/* Model Training */}
      {effectiveRole === 'admin' || effectiveRole === 'superadmin' && <ModelTraining />}

      {/* API Keys (admin only) */}
      {effectiveRole === 'admin' || effectiveRole === 'superadmin' && (
        <div className="border border-mono-800">
          <div className="border-b border-mono-800 px-4 py-3">
            <h3 className="text-xs text-mono-300 tracking-widest">API ACCESS</h3>
          </div>
          <div className="p-4">
            <p className="text-[10px] text-mono-500 tracking-wider mb-4">
              MANAGE API KEYS FOR PROGRAMMATIC ACCESS
            </p>
            <button className="px-4 py-2 border border-mono-600 text-mono-300 text-xs tracking-widest hover:border-mono-400 hover:text-mono-100 transition-colors">
              MANAGE KEYS
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
